import PasswordInput from './PasswordInput';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PasswordResetApi } from '../api/PasswordResetApi';

interface PasswordResetModalProps {
  isOpen: boolean;
  onClose: () => void;
  email?: string;
  mode: 'request' | 'reset' | 'change';
  token?: string;
  onSuccess?: () => void;
  isFirstLogin?: boolean;
}

export default function PasswordResetModal({ 
  isOpen, 
  onClose, 
  email = '', 
  mode, 
  token = '',
  onSuccess,
  isFirstLogin = false
}: PasswordResetModalProps) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    emailOrUsername: email,
    newPassword: '',
    confirmPassword: '',
    currentPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      if (mode === 'request') {
        if (!formData.emailOrUsername) {
          setError(t('passwordReset.emailRequired'));
          return;
        }
        await PasswordResetApi.requestPasswordReset(formData.emailOrUsername);
        setSuccess(t('passwordReset.requestSuccess'));
        setTimeout(() => {
          onClose();
          onSuccess?.();
        }, 2000);
      } 
      else if (mode === 'reset') {
        if (formData.newPassword !== formData.confirmPassword) {
          setError(t('passwordReset.passwordMismatch'));
          return;
        }
        if (formData.newPassword.length < 8) {
          setError(t('passwordReset.passwordTooShort'));
          return;
        }
        await PasswordResetApi.resetPassword({
          token,
          newPassword: formData.newPassword
        });
        setSuccess(t('passwordReset.resetSuccess'));
        setTimeout(() => {
          onClose();
          onSuccess?.();
        }, 2000);
      }
      else if (mode === 'change') {
        if (formData.newPassword !== formData.confirmPassword) {
          setError(t('passwordReset.passwordMismatch'));
          return;
        }
        if (formData.newPassword.length < 8) {
          setError(t('passwordReset.passwordTooShort'));
          return;
        }
        
        if (isFirstLogin) {
          // For first login, use forcePasswordReset API
          await PasswordResetApi.forcePasswordReset({
            username: email,
            currentPassword: formData.currentPassword,
            newPassword: formData.newPassword
          });
        } else {
          // For regular password change
          await PasswordResetApi.changePassword({
            currentPassword: formData.currentPassword,
            newPassword: formData.newPassword
          });
        }
        
        setSuccess(t('passwordReset.changeSuccess'));
        setTimeout(() => {
          onClose();
          onSuccess?.();
        }, 2000);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.message || t('passwordReset.genericError'));
    } finally {
      setLoading(false);
    }
  };

  const getTitle = () => {
    switch (mode) {
      case 'request': return t('passwordReset.requestTitle');
      case 'reset': return t('passwordReset.resetTitle');
      case 'change': 
        return isFirstLogin ? t('passwordReset.firstLoginTitle') : t('passwordReset.changeTitle');
      default: return t('passwordReset.title');
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: 12,
        padding: 32,
        minWidth: 400,
        maxWidth: 500,
        boxShadow: '0 4px 32px rgba(0, 0, 0, 0.2)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <h2 style={{ color: '#1976d2', margin: 0 }}>{getTitle()}</h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: 24,
              cursor: 'pointer',
              color: '#666'
            }}
          >
            ×
          </button>
        </div>

        {isFirstLogin && mode === 'change' && (
          <div style={{ 
            background: '#fff3cd', 
            border: '1px solid #ffeaa7', 
            borderRadius: 8, 
            padding: 16, 
            marginBottom: 16 
          }}>
            <p style={{ margin: 0, color: '#856404', fontSize: 14 }}>
              {t('passwordReset.firstLoginDescription')}
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {mode === 'request' && (
            <div>
              <p style={{ marginBottom: 16, color: '#666' }}>
                {t('passwordReset.requestDescription')}
              </p>
              <input
                type="text"
                placeholder={t('passwordReset.emailOrUsername')}
                value={formData.emailOrUsername}
                onChange={(e) => setFormData({ ...formData, emailOrUsername: e.target.value })}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  marginBottom: 16,
                  borderRadius: 8,
                  border: '1px solid #ddd',
                  fontSize: 16
                }}
                required
              />
            </div>
          )}

          {mode === 'change' && (
            <PasswordInput
              placeholder={t('passwordReset.currentPassword')}
              value={formData.currentPassword}
              onChange={(e) => setFormData({ ...formData, currentPassword: e.target.value })}
              style={{
                width: '100%',
                padding: '10px 12px',
                marginBottom: 16,
                borderRadius: 8,
                border: '1px solid #ddd',
                fontSize: 16
              }}
              required
            />
          )}

          {(mode === 'reset' || mode === 'change') && (
            <>
              <PasswordInput
                placeholder={t('passwordReset.newPassword')}
                value={formData.newPassword}
                onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  marginBottom: 16,
                  borderRadius: 8,
                  border: '1px solid #ddd',
                  fontSize: 16
                }}
                required
                minLength={8}
              />
              <PasswordInput
                placeholder={t('passwordReset.confirmPassword')}
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  marginBottom: 16,
                  borderRadius: 8,
                  border: '1px solid #ddd',
                  fontSize: 16
                }}
                required
                minLength={8}
              />
            </>
          )}

          {error && (
            <div style={{
              color: '#d32f2f',
              background: '#ffebee',
              padding: '8px 12px',
              borderRadius: 4,
              marginBottom: 16,
              fontSize: 14
            }}>
              {error}
            </div>
          )}

          {success && (
            <div style={{
              color: '#2e7d32',
              background: '#e8f5e9',
              padding: '8px 12px',
              borderRadius: 4,
              marginBottom: 16,
              fontSize: 14
            }}>
              {success}
            </div>
          )}

          <div style={{ display: 'flex', gap: 12 }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                flex: 1,
                padding: '12px 24px',
                borderRadius: 8,
                border: '1px solid #ddd',
                background: 'white',
                color: '#333',
                cursor: 'pointer',
                fontSize: 16
              }}
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                flex: 1,
                padding: '12px 24px',
                borderRadius: 8,
                border: 'none',
                background: loading ? '#ccc' : '#1976d2',
                color: 'white',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: 16
              }}
            >
              {loading ? t('common.loading') : t('common.submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
